import { useMemo, useState } from "react";
import {
  CartesianGrid, ComposedChart, LabelList, Line, ReferenceLine, ResponsiveContainer, Scatter, Tooltip, XAxis, YAxis,
} from "recharts";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import type { Row } from "./api";
import { comfortMove, day, L, ml } from "./format";
import { Empty } from "./parts";

// v3.1 PA3, "Planned journey to target": the dose as a step line on the left axis, each level badged with its plan
// step (a step-back repeats the earlier number), the planned climb dashed, check-ins as dots (filled taken, hollow
// missed), and weight / waist / results rating on their own right-hand scales, each toggled (threads #7, #13).
const num = (v: unknown) => (v != null && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null);
const ts = (d: string) => new Date(d.length === 10 ? `${d}T12:00:00Z` : d).getTime();
const C = {
  dose: "var(--color-ks-purple)", plan: "var(--color-ks-purple-2)", weight: "var(--color-ks-danger)",
  waist: "var(--color-ks-warn)", rating: "var(--color-ks-grey-1)", grid: "var(--color-ks-grey-4)", axis: "var(--color-ks-grey-1)",
};

const londonDay = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit" });
const londonDayIndex = (t: number) => Math.round(new Date(`${londonDay.format(new Date(t))}T00:00:00Z`).getTime() / 86_400_000);

type Point = { t: number; dose: number | null; step: number | null; badge: boolean };
type Dot = { t: number; y: number | null; took: boolean; c: Row };

/** The dose steps oldest first, each point badged where the level changes. Exported for the tests. */
export function doseSteps(checkins: Row[]): Point[] {
  const out: Point[] = [];
  for (const c of [...checkins].reverse()) {
    const dose = num(c.dose_ml);
    const prev = out[out.length - 1];
    out.push({ t: ts(c.at), dose, step: c.step ?? null, badge: !prev || prev.dose !== dose });
  }
  return out;
}

/**
 * The dose journey up to today: the check-ins, then today at the dose in force. A staff change since the last
 * check-in (plan.dose_ml / plan.step_index) shows as a step today, so the planned line starts where she is now.
 */
export function actualToNow(checkins: Row[], plan: Row, now: number): Point[] {
  const out = doseSteps(checkins);
  const last = out[out.length - 1];
  if (!last) return out;
  const dose = num(plan.dose_ml) ?? last.dose;
  out.push({ t: Math.max(now, last.t), dose, step: plan.step_index ?? last.step, badge: dose !== last.dose });
  return out;
}

export function ProgressChart({ plan, checkins, now }: { plan: Row; checkins: Row[]; now: number }) {
  const [show, setShow] = useState({ weight: true, waist: true, rating: false });
  const m = useMemo(() => {
    const actual = actualToNow(checkins, plan, now);
    const last = actual[actual.length - 1];
    const ahead: Point[] = ((plan.planned_ahead || []) as Row[]).map((p, i, a) => ({
      t: ts(p.date), dose: num(p.dose_ml), step: p.step ?? null,
      badge: (i === 0 ? last?.dose : num(a[i - 1].dose_ml)) !== num(p.dose_ml),
    }));
    const planned = last && ahead.length ? [{ ...last, badge: false }, ...ahead] : [];
    const old = [...checkins].reverse();
    // Filled = she took some; hollow = nothing taken: a missed week, or a check-in answering "didn't take it".
    const tookSome = (c: Row) => c.took_choice !== "didnt" && num(c.took_ml) !== 0;
    const answered = old.filter((c) => c.kind !== "start");
    const taken: Dot[] = answered.filter(tookSome).map((c) => ({ t: ts(c.at), y: num(c.took_ml) ?? num(c.previous_dose_ml), took: true, c }));
    const missedDots: Dot[] = [
      ...answered.filter((c) => !tookSome(c)).map((c) => ({ t: ts(c.at), y: num(c.previous_dose_ml), took: false, c })),
      ...((plan.missed_checkins || []) as Row[]).map((x) => ({
        t: ts(x.date), y: num(x.dose_ml), took: false, c: { ...x, at: x.date, missed: true, previous_dose_ml: x.dose_ml },
      })),
    ];
    const series = (k: string) => old.map((c) => ({ t: ts(c.at), v: num(c[k]) })).filter((p) => p.v != null);
    const byT = new Map<number, Row>();
    for (const d of [...taken, ...missedDots]) byT.set(d.t, d.c);
    const xs = [...new Set([...actual, ...ahead, ...missedDots].map((p) => p.t))].sort((a, b) => a - b);
    return { actual, planned, taken, missedDots, weight: series("weight_kg"), waist: series("waist_cm"), rating: series("results_score"), byT, xs };
  }, [plan.planned_ahead, plan.missed_checkins, plan.dose_ml, plan.step_index, checkins, now]);

  if (!m.actual.length) return <Empty>No check-ins yet: the journey starts at the first one.</Empty>;
  const target = num(plan.target_dose_ml);
  // Day N as the API counts it: calendar days in London, Day 1 = started_on.
  const dayN = (t: number) => (plan.started_on ? londonDayIndex(t) - londonDayIndex(ts(plan.started_on)) + 1 : null);
  const todayN = dayN(now);
  const flags = ((plan.stomach_changes || []) as Row[]).filter((x) => x.at);

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="text-ks-grey-1">Left: Dose (ml)</span>
        <span className="text-ks-grey-1">Right:</span>
        {([["weight", "Weight (kg)"], ["waist", "Waist (cm)"], ["rating", "Results rating"]] as const).map(([k, t]) => (
          <Label key={k} className="font-normal">
            <Checkbox data-nonmutating checked={show[k]} onCheckedChange={(c) => setShow((s) => ({ ...s, [k]: c === true }))} /> {t}
          </Label>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart margin={{ top: 24, right: 8, bottom: 8, left: 0 }} accessibilityLayer>
          <CartesianGrid strokeDasharray="3 3" stroke={C.grid} />
          <XAxis dataKey="t" type="number" scale="time" domain={["dataMin", "dataMax"]} ticks={m.xs} stroke={C.axis} fontSize={11}
            height={36} tick={<DayTick dayN={dayN} />} allowDuplicatedCategory={false} />
          <YAxis yAxisId="dose" stroke={C.dose} fontSize={11} domain={[0, (max: number) => Math.max(max, target ?? 0) + 5]} unit=" ml" width={52} />
          {show.weight ? <YAxis yAxisId="weight" orientation="right" stroke={C.weight} fontSize={11} domain={["auto", "auto"]} unit=" kg" width={52} /> : null}
          {show.waist ? <YAxis yAxisId="waist" orientation="right" stroke={C.waist} fontSize={11} domain={["auto", "auto"]} unit=" cm" width={52} /> : null}
          {show.rating ? <YAxis yAxisId="rating" orientation="right" stroke={C.rating} fontSize={11} domain={[0, 10]} width={32} /> : null}
          {target != null ? (
            <ReferenceLine yAxisId="dose" y={target} stroke={C.dose} strokeDasharray="2 4"
              label={{ value: `Target ${ml(target)}`, position: "insideTopLeft", fontSize: 11, fill: C.dose }} />
          ) : null}
          <ReferenceLine yAxisId="dose" x={now} stroke={C.axis}
            label={{ value: `Today${todayN != null ? `, Day ${todayN}` : ""}`, position: "top", fontSize: 11, fill: C.axis }} />
          {flags.map((f) => (
            <ReferenceLine key={`${f.at}-${f.to}`} yAxisId="dose" x={ts(f.at)} stroke={C.waist} strokeDasharray="4 2"
              label={{ value: `${comfortMove(f.from, f.to).replace(/^s/, "S")} (${f.by === "staff" ? "staff" : "algorithm"})`, position: "insideTopRight", fontSize: 11, fill: C.axis }} />
          ))}
          <Line yAxisId="dose" data={m.actual} dataKey="dose" type="stepAfter" stroke={C.dose} strokeWidth={2.5} dot={false}
            isAnimationActive={false} name="Dose">
            <LabelList dataKey="dose" content={<StepBadge points={m.actual} />} />
          </Line>
          {m.planned.length ? (
            <Line yAxisId="dose" data={m.planned} dataKey="dose" type="stepAfter" stroke={C.plan} strokeWidth={2}
              strokeDasharray="6 4" dot={false} isAnimationActive={false} name="Planned ahead">
              <LabelList dataKey="dose" content={<StepBadge points={m.planned} />} />
            </Line>
          ) : null}
          <Scatter yAxisId="dose" data={m.taken} dataKey="y" fill={C.dose} name="Took" isAnimationActive={false} />
          <Scatter yAxisId="dose" data={m.missedDots} dataKey="y" fill="var(--color-ks-surface)" stroke={C.dose} name="Missed"
            isAnimationActive={false} />
          {show.weight ? (
            <Line yAxisId="weight" data={m.weight} dataKey="v" stroke={C.weight} strokeWidth={2} connectNulls
              dot={{ r: 3, fill: C.weight }} isAnimationActive={false} name="Weight" />
          ) : null}
          {show.waist ? (
            <Line yAxisId="waist" data={m.waist} dataKey="v" stroke={C.waist} strokeWidth={2} strokeDasharray="5 3" connectNulls
              dot={{ r: 3, fill: "var(--color-ks-surface)" }} isAnimationActive={false} name="Waist" />
          ) : null}
          {show.rating ? (
            <Line yAxisId="rating" data={m.rating} dataKey="v" stroke={C.rating} strokeWidth={1.5} connectNulls
              dot={{ r: 2 }} isAnimationActive={false} name="Results rating" />
          ) : null}
          <Tooltip content={<CheckinCard byT={m.byT} dayN={dayN} />} />
        </ComposedChart>
      </ResponsiveContainer>
      <p className="mt-1 flex flex-wrap gap-x-4 text-xs text-ks-grey-1">
        <span>Dose, labelled with its plan step</span>
        <span>Planned ahead</span>
        <span>● took · ○ missed (hover or focus for the check-in)</span>
        <span>◆ Weight, solid</span>
        <span>□ Waist, dashed</span>
      </p>
    </div>
  );
}

/** x axis: the date, and "Day N" beneath it. */
function DayTick({ x, y, payload, dayN }: { x?: number; y?: number; payload?: { value: number }; dayN: (t: number) => number | null }) {
  if (x == null || y == null || !payload) return null;
  const n = dayN(payload.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={12} textAnchor="middle" fontSize={11} fill={C.axis}>{day(payload.value)}</text>
      {n != null ? <text dy={25} textAnchor="middle" fontSize={10} fill={C.axis}>Day {n}</text> : null}
    </g>
  );
}

/** "Step 4 · 12.5 ml" where the dose level changes. */
function StepBadge({ x, y, index, points }: { x?: number | string; y?: number | string; index?: number; points: Point[] }) {
  const p = index != null ? points[index] : undefined;
  if (!p || !p.badge || p.dose == null || x == null || y == null) return null;
  const text = p.step != null ? `Step ${p.step} · ${p.dose} ml` : `${p.dose} ml`;
  return (
    <text x={Number(x) + 4} y={Number(y) - 6} fontSize={10} fontWeight={600} fill={C.dose}>{text}</text>
  );
}

/** The hover card (keyboard reachable through the chart's accessibility layer): the check-in in words. */
function CheckinCard({ active, label, byT, dayN }: { active?: boolean; label?: number; byT: Map<number, Row>; dayN: (t: number) => number | null }) {
  if (!active || label == null) return null;
  const c = byT.get(Number(label));
  if (!c) return null;
  const n = c.day ?? dayN(Number(label));
  const bits = [
    `${day(Number(label))}${n != null ? ` · Day ${n}` : ""} check-in`,
    `target ${ml(c.previous_dose_ml ?? c.dose_ml)}`,
    c.missed ? "took nothing (missed)" : c.took_choice === "didnt" ? "took nothing" : `took ${ml(c.took_ml)}`,
    `stomach: ${c.missed ? "Missed" : L(c.stomach)}`,
    !c.missed && c.weight_kg != null ? `weight ${c.weight_kg} kg` : null,
    !c.missed && c.waist_cm != null ? `waist ${c.waist_cm} cm` : null,
  ].filter(Boolean);
  return (
    <div className="rounded-lg border border-ks-grey-3 bg-ks-surface p-2 text-xs text-ks-ink shadow-sm">{bits.join(" · ")}</div>
  );
}
